/**
 * AppSidebar — AI-native collapsible navigation sidebar.
 *
 * Features:
 * - 240px expanded / 56px collapsed with smooth CSS transition
 * - Section headers: WORKSPACE / SCIENCE / HISTORY / INTEGRATIONS
 * - Active state highlights using current route
 * - Permission gating: hidden items shown as locked (Lock icon) in expanded mode
 * - Tooltip on collapsed items showing full label
 * - Collapse toggle button at the bottom
 * - Reads permissions from usePermissions() (API-driven, per-tenant)
 */

import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Tooltip } from "antd";
import { useLayout } from "../../contexts/LayoutContext";
import { usePermissions } from "../../hooks/usePermissions";
import { ENABLED_NAV_PATHS } from "../../constants/navGate";

// ── Icons ─────────────────────────────────────────────────────────────────────

const Icon = ({ d, size = 16 }: { d: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const HomeIcon       = () => <Icon d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M9 22V12h6v10" />;
const ChatIcon       = () => <Icon d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />;
const PerformanceAgentIcon  = () => <Icon d="M22 12h-4l-3 9L9 3l-3 9H2" />;
const BuilderIcon    = () => <Icon d="M12 8V4H8M4 12a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zM2 14h2m16 0h2M15 13v2m-6-2v2" />;
const FlaskIcon      = () => <Icon d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18" />;
const CalculatorIcon = () => <Icon d="M4 2h16a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zM8 6h2m6 0h2M8 10h2m2 0h2m2 0h2M8 14h8M8 18h8" />;
const ChartIcon      = () => <Icon d="M18 20V10M12 20V4M6 20v-6" />;
const TargetIcon     = () => <Icon d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zm0 0V12M12 2v4m0 12v4M2 12h4m12 0h4" />;
const HistoryIcon    = () => <Icon d="M3 3v5h5M3.05 13A9 9 0 1 0 6 5.3L3 8" />;
const ReportIcon     = () => <Icon d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8" />;
const EarthIcon      = () => <Icon d="M2 12a10 10 0 1 0 20 0A10 10 0 0 0 2 12zm8-8C8.5 7 8 9 8 12s.5 5 2 8m4-16c1.5 3 2 5 2 8s-.5 5-2 8M2 12h20" />;
const CRMIcon        = () => <Icon d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2m8-10a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm12 10v-2a4 4 0 0 0-3-3.87m-4-12a4 4 0 0 1 0 7.75" />;
const ShopIcon       = () => <Icon d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4zM3 6h18M16 10a4 4 0 0 1-8 0" />;
const CommandIcon    = () => <Icon d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" />;
const LockIcon       = () => <Icon d="M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2zm-7-8a4 4 0 0 1 4 4v4H8V7a4 4 0 0 1 4-4z" />;
const ChevronIcon    = ({ expanded }: { expanded: boolean }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: expanded ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 200ms ease' }}>
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

// ── Menu definition ───────────────────────────────────────────────────────────

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  permissionKey: string;     // key in TenantFeatures / UserPermissions
  section?: string;
  badge?: number;
  /** Small text tag after the label, e.g. "+ ML". */
  tag?: { text: string; tone: "blue" | "violet" };
  /** Sub-items shown under this item when the sidebar is expanded. */
  children?: NavChild[];
}

interface NavChild {
  label: string;
  path: string;
  /** Extra path prefixes that also mark this child active (e.g. a list's detail pages). */
  alsoActive?: string[];
}

// Command Center (demo): two menus with the same four pages each.
const commandCenterChildren = (base: string): NavChild[] => [
  { label: "All tests",        path: base, alsoActive: [`${base}/new`, `${base}/tests`] },
  { label: "Calendar",         path: `${base}/calendar` },
  { label: "Learning library", path: `${base}/library` },
  { label: "Guardrail rules",  path: `${base}/rules` },
];

const NAV_SECTIONS: { id: string; label?: string; items: NavItem[] }[] = [
  {
    id: "core",
    items: [
      { label: "Dashboard", path: "/", icon: <HomeIcon />, permissionKey: "home" },
    ],
  },
  {
    id: "workspace",
    label: "WORKSPACE",
    items: [
      { label: "Ask Adrsta",     path: "/workspace",    icon: <ChatIcon />,       permissionKey: "ai_assistant",  badge: 0 },
      { label: "Agent Builder",  path: "/agent-builder", icon: <BuilderIcon />,   permissionKey: "ai_assistant" },
      { label: "Performance Agent",      path: "/performance-agent",    icon: <PerformanceAgentIcon />,  permissionKey: "performance_agent" },
    ],
  },
  {
    id: "science",
    label: "SCIENCE",
    items: [
      { label: "Command Center",   path: "/command-center/ml",   icon: <CommandIcon />, permissionKey: "experiments",
        tag: { text: "+ ML", tone: "blue" },   children: commandCenterChildren("/command-center/ml") },
      { label: "Command Center",   path: "/command-center/deck", icon: <CommandIcon />, permissionKey: "experiments",
        tag: { text: "NO ML", tone: "violet" }, children: commandCenterChildren("/command-center/deck") },
      { label: "Experiments",      path: "/experiments",      icon: <FlaskIcon />,      permissionKey: "experiments" },
      { label: "Power Calculator", path: "/power-calculator", icon: <CalculatorIcon />, permissionKey: "power_calculator" },
      { label: "MMM",              path: "/mmm",              icon: <ChartIcon />,      permissionKey: "mmm" },
      { label: "Bid Optimizer",    path: "/bid-optimizer",    icon: <TargetIcon />,     permissionKey: "bid_optimizer" },
    ],
  },
  {
    id: "history",
    label: "HISTORY",
    items: [
      { label: "Past Work", path: "/history", icon: <HistoryIcon />, permissionKey: "history" },
      { label: "Reports",   path: "/reports", icon: <ReportIcon />,  permissionKey: "reports" },
    ],
  },
  {
    id: "integrations",
    label: "INTEGRATIONS",
    items: [
      { label: "Media Channels",    path: "/facebook/integrations",   icon: <EarthIcon />, permissionKey: "integrations_media" },
      { label: "CRM Channels",      path: "/crm/integrations",        icon: <CRMIcon />,   permissionKey: "integrations_crm" },
      { label: "E-commerce",        path: "/e-commerce/integrations", icon: <ShopIcon />,  permissionKey: "integrations_ecommerce" },
    ],
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

export default function AppSidebar() {
  const { isSidebarExpanded, setSidebarExpanded } = useLayout();
  const { checkMenuPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string): boolean => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const handleNav = (path: string, hasPermission: boolean) => {
    if (hasPermission) navigate(path);
  };

  return (
    <>
      <style>{`
        .adrsta-sidebar {
          width: var(--sidebar-w);
          min-width: var(--sidebar-w);
          transition: width 200ms ease, min-width 200ms ease;
          background: var(--bg-surface);
          border-right: 1px solid var(--border-default);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          flex-shrink: 0;
          height: 100%;
        }
        .adrsta-sidebar.collapsed {
          width: var(--sidebar-w-collapsed);
          min-width: var(--sidebar-w-collapsed);
        }
        .nav-section-label {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.09em;
          text-transform: uppercase;
          color: var(--text-tertiary);
          padding: 12px 12px 4px;
          white-space: nowrap;
          overflow: hidden;
          opacity: 1;
          transition: opacity 150ms ease;
          font-family: var(--font-sans);
        }
        .adrsta-sidebar.collapsed .nav-section-label {
          opacity: 0;
          height: 0;
          padding: 0;
        }
        .nav-divider {
          height: 1px;
          background: var(--border-subtle);
          margin: 6px 10px;
        }
        .nav-item {
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 7px 10px;
          margin: 1px 6px;
          border-radius: var(--radius-sm);
          cursor: pointer;
          transition: background 120ms ease, color 120ms ease;
          color: var(--text-secondary);
          font-size: 13.5px;
          font-family: var(--font-sans);
          font-weight: 400;
          white-space: nowrap;
          text-decoration: none;
          min-height: 34px;
        }
        .nav-item:hover {
          background: var(--bg-hover);
          color: var(--text-primary);
        }
        .nav-item.active {
          background: var(--blue-subtle);
          color: var(--blue);
          font-weight: 500;
        }
        .nav-item.locked {
          cursor: not-allowed;
          opacity: 0.45;
        }
        .nav-item .icon-wrap {
          width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .nav-item .label {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          transition: opacity 150ms ease, width 150ms ease;
        }
        .adrsta-sidebar.collapsed .nav-item .label,
        .adrsta-sidebar.collapsed .nav-badge {
          opacity: 0;
          width: 0;
          overflow: hidden;
        }
        .nav-badge {
          background: var(--blue);
          color: #fff;
          font-size: 10px;
          font-weight: 600;
          padding: 1px 5px;
          border-radius: 10px;
          min-width: 18px;
          text-align: center;
          flex-shrink: 0;
          transition: opacity 150ms ease;
        }
        .nav-tag {
          font-size: 9.5px;
          font-weight: 700;
          letter-spacing: 0.04em;
          padding: 1px 6px;
          border-radius: 10px;
          flex-shrink: 0;
          transition: opacity 150ms ease;
        }
        .nav-tag.blue   { background: var(--blue-subtle);   color: var(--blue); }
        .nav-tag.violet { background: var(--violet-subtle); color: var(--violet); }
        .adrsta-sidebar.collapsed .nav-tag { opacity: 0; width: 0; padding: 0; overflow: hidden; }
        .nav-item.sub {
          margin-left: 33px;
          font-size: 13px;
          min-height: 30px;
          padding: 5px 10px;
        }
        .sidebar-collapse-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 10px;
          margin: 4px 6px;
          border-radius: var(--radius-sm);
          color: var(--text-tertiary);
          font-size: 12px;
          font-family: var(--font-sans);
          cursor: pointer;
          transition: background 120ms ease, color 120ms ease;
          white-space: nowrap;
        }
        .sidebar-collapse-btn:hover {
          background: var(--bg-hover);
          color: var(--text-secondary);
        }
      `}</style>

      <nav className={`adrsta-sidebar${isSidebarExpanded ? '' : ' collapsed'}`}>
        {/* ── Scrollable nav area ────────────────────────────────────────── */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '8px 0' }}>
          {NAV_SECTIONS.map((section, sIdx) => (
            <div key={section.id}>
              {/* Section label */}
              {section.label && (
                <div className="nav-section-label">{section.label}</div>
              )}

              {/* Section items */}
              {section.items.map((item) => {
                const hasPermission = ENABLED_NAV_PATHS.has(item.path) && checkMenuPermission(item.path);
                const active = isActive(item.path);

                const navEl = (
                  <div
                    key={item.path}
                    className={`nav-item${active ? ' active' : ''}${!hasPermission ? ' locked' : ''}`}
                    onClick={() => handleNav(item.path, hasPermission)}
                  >
                    <span className="icon-wrap">
                      {!hasPermission ? <LockIcon /> : item.icon}
                    </span>
                    <span className="label">{item.label}</span>
                    {item.tag && (
                      <span className={`nav-tag ${item.tag.tone}`}>{item.tag.text}</span>
                    )}
                    {(item.badge ?? 0) > 0 && (
                      <span className="nav-badge">{item.badge}</span>
                    )}
                  </div>
                );

                // Wrap in Tooltip when collapsed
                if (!isSidebarExpanded) {
                  return (
                    <Tooltip key={item.path} title={item.tag ? `${item.label} (${item.tag.text})` : item.label} placement="right">
                      {navEl}
                    </Tooltip>
                  );
                }
                if (item.children && hasPermission) {
                  return (
                    <React.Fragment key={item.path}>
                      {navEl}
                      {item.children.map(child => {
                        const childActive = location.pathname === child.path
                          || (child.path !== item.path && location.pathname.startsWith(child.path))
                          || (child.alsoActive ?? []).some(p => location.pathname.startsWith(p));
                        return (
                          <div
                            key={child.path}
                            className={`nav-item sub${childActive ? ' active' : ''}`}
                            onClick={() => navigate(child.path)}
                          >
                            <span className="label">{child.label}</span>
                          </div>
                        );
                      })}
                    </React.Fragment>
                  );
                }
                return navEl;
              })}

              {/* Divider before integrations section */}
              {section.id === "history" && <div className="nav-divider" />}
            </div>
          ))}
        </div>

        {/* ── Collapse toggle ────────────────────────────────────────────── */}
        <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '4px 0 8px' }}>
          {!isSidebarExpanded ? (
            <Tooltip title="Expand sidebar" placement="right">
              <button
                onClick={() => setSidebarExpanded(true)}
                className="sidebar-collapse-btn w-full justify-center"
              >
                <ChevronIcon expanded={false} />
              </button>
            </Tooltip>
          ) : (
            <button
              onClick={() => setSidebarExpanded(false)}
              className="sidebar-collapse-btn"
            >
              <ChevronIcon expanded={true} />
              <span>Collapse</span>
            </button>
          )}
        </div>
      </nav>
    </>
  );
}
